import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { withRouter } from 'react-router';
import { colors } from '@guryou/colors';
import { Modal } from '@guryou/html-components';
import Contact from 'containers/ContactUs';
import DownloadApp from '../../DownloadApp';

const Header = () => {
  const { t, i18n } = useTranslation();
  const [openDownload, setOpenDownload] = useState(false);
  const [openContact, setOpenContact] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const currentLanguage = i18n.language;

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  ];

  const handleLanguage = lang => {
    i18n.changeLanguage(lang);
    setIsLanguageDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (isLanguageDropdownOpen && !event.target.closest('[data-language-dropdown]')) {
        setIsLanguageDropdownOpen(false);
      }
    };

    if (isLanguageDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLanguageDropdownOpen]);

  return (
    <div className="absolute w-full z-50 top-0">
      {/* Top Navigation Bar */}
      <nav className="border-gray-700" style={{ backgroundColor: colors.mineShaft }}>
        <div className="max-w-screen-xl flex flex-wrap items-center justify-end mx-auto p-2 md:p-3">
          {/* Desktop Menu - Icons with Labels */}
          <div className="hidden md:flex items-center space-x-4">
            {/* About */}
            <button
              onClick={() => window.location.replace('/about')}
              className={`flex items-center space-x-2 p-1.5 text-sm rounded-lg focus:outline-none focus:ring-2 transition-colors text-white hover:bg-[${colors.brownMineShaft}]`}
              style={{
                '--focus-ring-color': 'var(--feel-primary)',
              }}
              title="About Us"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                ></path>
              </svg>
              <span>{t('NAVBAR.about')}</span>
            </button>

            {/* Contact */}
            <button
              onClick={() => setOpenContact(true)}
              className={`flex items-center space-x-2 p-1.5 text-sm rounded-lg focus:outline-none focus:ring-2 transition-colors text-white hover:bg-[${colors.brownMineShaft}]`}
              style={{
                '--focus-ring-color': 'var(--feel-primary)',
              }}
              title="Contact Us"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
              </svg>
              <span>{t('NAVBAR.contactUs')}</span>
            </button>

            {/* Register Business */}
            <button
              onClick={() => window.location.replace(process.env.REACT_APP_GURYOU_BIZZ)}
              className={`flex items-center space-x-2 p-1.5 text-sm rounded-lg focus:outline-none focus:ring-2 transition-colors text-white hover:bg-[${colors.brownMineShaft}]`}
              style={{
                '--focus-ring-color': 'var(--feel-primary)',
              }}
              title="Register Business"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path
                  fillRule="evenodd"
                  d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm2 6a2 2 0 114 0 2 2 0 01-4 0zm8 0a2 2 0 114 0 2 2 0 01-4 0z"
                  clipRule="evenodd"
                ></path>
              </svg>
              <span>{t('NAVBAR.registerBusiness')}</span>
            </button>

            {/* Download App */}
            <button
              onClick={() => setOpenDownload(true)}
              className={`flex items-center space-x-2 p-1.5 text-sm rounded-lg focus:outline-none focus:ring-2 transition-colors text-white hover:bg-[${colors.brownMineShaft}]`}
              style={{
                '--focus-ring-color': 'var(--feel-primary)',
              }}
              title="Download App"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path
                  fillRule="evenodd"
                  d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                  clipRule="evenodd"
                ></path>
              </svg>
              <span>{t('NAVBAR.downloadApp')}</span>
            </button>

            {/* Language Dropdown */}
            <div className="relative" data-language-dropdown>
              <button
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                className={`flex items-center space-x-2 p-1.5 text-sm rounded-lg focus:outline-none focus:ring-2 transition-colors text-white hover:bg-[${colors.brownMineShaft}]`}
                style={{
                  '--focus-ring-color': 'var(--feel-primary)',
                }}
              >
                <span className="text-xl">{languages.find(lang => lang.code === currentLanguage)?.flag || '🌐'}</span>
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  ></path>
                </svg>
              </button>

              {/* Dropdown Menu */}
              {isLanguageDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="py-1">
                    {languages.map(language => (
                      <button
                        key={language.code}
                        onClick={() => handleLanguage(language.code)}
                        className={`flex items-center w-full px-4 py-2 text-sm transition-colors ${
                          currentLanguage === language.code ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span className="text-xl mr-3">{language.flag}</span>
                        <span>{language.name}</span>
                        {currentLanguage === language.code && (
                          <svg className="w-4 h-4 ml-auto text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            ></path>
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Actions - Links Left, Language Right */}
          <div className="md:hidden flex items-center justify-between w-full">
            {/* Left Side - Action Links */}
            <div className="flex items-center space-x-2">
              {/* About */}
              <button
                onClick={() => window.location.replace('/about')}
                className={`flex flex-col items-center p-1 text-xs rounded-lg focus:outline-none focus:ring-2 transition-colors text-white hover:bg-[${colors.brownMineShaft}]`}
                style={{
                  '--focus-ring-color': 'var(--feel-primary)',
                }}
              >
                <svg className="w-4 h-4 mb-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                <span className="text-xs font-medium">About</span>
              </button>

              {/* Contact */}
              <button
                onClick={() => setOpenContact(true)}
                className={`flex flex-col items-center p-1 text-xs rounded-lg focus:outline-none focus:ring-2 transition-colors text-white hover:bg-[${colors.brownMineShaft}]`}
                style={{
                  '--focus-ring-color': 'var(--feel-primary)',
                }}
              >
                <svg className="w-4 h-4 mb-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
                </svg>
                <span className="text-xs font-medium">Contact</span>
              </button>

              {/* Register Business */}
              <button
                onClick={() => window.location.replace(process.env.REACT_APP_GURYOU_BIZZ)}
                className={`flex flex-col items-center p-1 text-xs rounded-lg focus:outline-none focus:ring-2 transition-colors text-white hover:bg-[${colors.brownMineShaft}]`}
                style={{
                  '--focus-ring-color': 'var(--feel-primary)',
                }}
              >
                <svg className="w-4 h-4 mb-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fillRule="evenodd"
                    d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm2 6a2 2 0 114 0 2 2 0 01-4 0zm8 0a2 2 0 114 0 2 2 0 01-4 0z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                <span className="text-xs font-medium">Register</span>
              </button>

              {/* Download App */}
              <button
                onClick={() => setOpenDownload(true)}
                className={`flex flex-col items-center p-1 text-xs rounded-lg focus:outline-none focus:ring-2 transition-colors text-white hover:bg-[${colors.brownMineShaft}]`}
                style={{
                  '--focus-ring-color': 'var(--feel-primary)',
                }}
              >
                <svg className="w-4 h-4 mb-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fillRule="evenodd"
                    d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                <span className="text-xs font-medium">Download</span>
              </button>
            </div>

            {/* Right Side - Language Dropdown */}
            <div className="relative" data-language-dropdown>
              <button
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                className={`flex flex-col items-center p-1 text-xs rounded-lg focus:outline-none focus:ring-2 transition-colors text-white hover:bg-[${colors.brownMineShaft}]`}
                style={{
                  '--focus-ring-color': 'var(--feel-primary)',
                }}
              >
                <div className="flex items-center space-x-1 mb-1">
                  <span className="text-2xl">{languages.find(lang => lang.code === currentLanguage)?.flag || '🌐'}</span>
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                </div>
              </button>

              {/* Mobile Dropdown Menu */}
              {isLanguageDropdownOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="py-1">
                    {languages.map(language => (
                      <button
                        key={language.code}
                        onClick={() => handleLanguage(language.code)}
                        className={`flex items-center w-full px-3 py-2 text-sm transition-colors ${
                          currentLanguage === language.code ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span className="text-2xl mr-2">{language.flag}</span>
                        <span className="text-xs">{language.name}</span>
                        {currentLanguage === language.code && (
                          <svg className="w-3 h-3 ml-auto text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            ></path>
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Modals */}
      <Modal title={t('NAVBAR.contactUs')} open={openContact} id="contactUs" close={() => setOpenContact(false)}>
        <Contact />
      </Modal>
      <Modal id="downloadApp" close={() => setOpenDownload(false)} open={openDownload} title={t('NAVBAR.downloadApp')}>
        <DownloadApp />
      </Modal>
    </div>
  );
};

export default withRouter(Header);
